import { useCallback, useEffect, useState, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormSection } from '@/components/shared/FormSection';
import { EditPageLayout } from '@/components/shared/EditPageLayout';
import { useIndustryTranslation } from '@/hooks/useIndustryTranslation';
import type { Scanner, Branch, Shelf, User, Tenant, DeviceModel } from '@packages/shared-types';
import {
  getScannerById,
  type UpdateScannerPayload,
  updateScanner,
  deleteScanner,
} from '@/services/scannerService';
import { listBranches } from '@/services/branchService';
import { listShelves } from '@/services/shelfService';
import { listUsers } from '@/services/userService';
import { listDeviceModels } from '@/services/deviceModelService';
import { getTenants } from '@/services/tenantService';
import { useTenantSwitcher } from '@/contexts/TenantSwitcherContext';
import { useAuth } from '@/hooks/useAuth';

const scannerFormSchema = z.object({
  name: z.string().min(1, 'pages.scanners.form.nameRequired'),
  deviceId: z.string().min(1, 'pages.scanners.form.deviceIdRequired'),
  type: z.enum(['handheld', 'fixed', 'standalone']),
  status: z.enum(['active', 'inactive', 'maintenance', 'charging']),
  tenantId: z.string().uuid().optional().or(z.literal('')),
  branchId: z.string().uuid().min(1, 'pages.scanners.form.branchRequired'),
  shelfId: z.string().uuid().optional().or(z.literal('')),
  assignedUserId: z.string().uuid().optional().or(z.literal('')),
  deviceModelId: z.string().optional(),
});

type ScannerFormValues = z.infer<typeof scannerFormSchema>;

const SCANNER_TYPES: Array<{ value: 'handheld' | 'fixed' | 'standalone'; labelKey: string }> = [
  { value: 'handheld', labelKey: 'pages.scanners.types.handheld' },
  { value: 'fixed', labelKey: 'pages.scanners.types.fixed' },
  { value: 'standalone', labelKey: 'pages.scanners.types.standalone' },
];

const SCANNER_STATUSES: Array<{
  value: 'active' | 'inactive' | 'maintenance' | 'charging';
  labelKey: string;
}> = [
  { value: 'active', labelKey: 'pages.scanners.statuses.active' },
  { value: 'inactive', labelKey: 'pages.scanners.statuses.inactive' },
  { value: 'maintenance', labelKey: 'pages.scanners.statuses.maintenance' },
  { value: 'charging', labelKey: 'pages.scanners.statuses.charging' },
];

export function EditScannerPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { activeTenant } = useTenantSwitcher();
  const { user } = useAuth();
  const isRoot = user?.role === 'root';

  // Redirect non-root users and root users in tenant context to detail page
  useEffect(() => {
    if ((!isRoot || activeTenant) && id) {
      navigate(`/scanners/${id}`, { replace: true });
    }
  }, [isRoot, activeTenant, id, navigate]);
  const editTitle = useIndustryTranslation('pages.scanners.edit.title');
  const editDescription = useIndustryTranslation('pages.scanners.edit.description');
  const deleteDialogTitle = useIndustryTranslation('pages.scanners.dialogs.delete.title');
  const successUpdate = useIndustryTranslation('pages.scanners.success.update');
  const successDelete = useIndustryTranslation('pages.scanners.success.delete');
  const errorLoad = useIndustryTranslation('pages.scanners.error.load');
  const errorUpdate = useIndustryTranslation('pages.scanners.error.update');
  const errorDelete = useIndustryTranslation('pages.scanners.error.delete');
  const errorNotFound = useIndustryTranslation('pages.scanners.error.notFound');
  const branchLabel = useIndustryTranslation('pages.scanners.form.branch');
  const shelfLabel = useIndustryTranslation('pages.scanners.form.shelf');
  const selectShelfLabel = useIndustryTranslation('pages.scanners.form.selectShelf');

  const [scanner, setScanner] = useState<Scanner | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [deviceModels, setDeviceModels] = useState<DeviceModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [isLoadingShelves, setIsLoadingShelves] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ScannerFormValues>({
    resolver: zodResolver(scannerFormSchema),
    defaultValues: {
      name: '',
      deviceId: '',
      type: 'handheld',
      status: 'active',
      tenantId: '',
      branchId: '',
      shelfId: '',
      assignedUserId: '',
      deviceModelId: '__none__',
    },
  });

  const selectedBranchId = watch('branchId');
  const selectedTenantId = watch('tenantId');

  // Filter shelves by selected branch
  const availableShelves = useMemo(() => {
    if (!selectedBranchId) return [];
    return shelves.filter((shelf) => shelf.branchId === selectedBranchId);
  }, [shelves, selectedBranchId]);

  const formatErrorMessage = useCallback(
    (message?: string): string | null => {
      if (!message) {
        return null;
      }

      return t(message);
    },
    [t],
  );

  const loadScanner = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      // Load scanner first so we have tenantId for support users (root without activeTenant)
      const scannerData = await getScannerById(id);
      setScanner(scannerData);
      const effectiveTenantId = activeTenant?.id ?? scannerData.tenantId ?? undefined;

      const [branchesData, shelvesData, usersData, deviceModelsData] =
        await Promise.all([
          listBranches(effectiveTenantId),
          listShelves(effectiveTenantId),
          listUsers(effectiveTenantId),
          listDeviceModels(),
        ]);

      setDeviceModels(deviceModelsData);
      setBranches(branchesData);
      setShelves(shelvesData);
      setUsers(usersData);

      reset({
        name: scannerData.name,
        deviceId: scannerData.deviceId,
        type: scannerData.type,
        status: scannerData.status,
        tenantId: scannerData.tenantId || '',
        branchId: scannerData.branchId,
        shelfId: scannerData.shelfId || '',
        assignedUserId: scannerData.assignedUserId || '',
        deviceModelId: scannerData.deviceModelId || '__none__',
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : errorLoad,
      );
    } finally {
      setIsLoading(false);
    }
  }, [id, reset, errorLoad, activeTenant?.id]);

  useEffect(() => {
    void loadScanner();
  }, [loadScanner]);

  // Fetch tenants (only for root users)
  useEffect(() => {
    if (!isRoot) return;
    const loadTenants = async (): Promise<void> => {
      setIsLoadingTenants(true);
      try {
        const data = await getTenants();
        setTenants(data);
      } catch (err) {
        console.error('Failed to load tenants', err);
      } finally {
        setIsLoadingTenants(false);
      }
    };
    void loadTenants();
  }, [isRoot]);

  // Load branches, shelves, and users when tenant selection changes (root dropdown or active tenant)
  useEffect(() => {
    const effectiveTenantId =
      isRoot && selectedTenantId ? selectedTenantId : activeTenant?.id ?? scanner?.tenantId ?? undefined;
    const loadData = async (): Promise<void> => {
      setIsLoadingBranches(true);
      setIsLoadingShelves(true);
      setIsLoadingUsers(true);
      try {
        const [branchesData, shelvesData, usersData] = await Promise.all([
          listBranches(effectiveTenantId),
          listShelves(effectiveTenantId),
          listUsers(effectiveTenantId),
        ]);
        // Filter branches by tenant if we have one
        const filteredBranches = effectiveTenantId
          ? branchesData.filter((branch) => branch.tenantId === effectiveTenantId)
          : branchesData;
        setBranches(filteredBranches);
        setShelves(shelvesData);
        setUsers(usersData);
      } catch (err) {
        console.error('Failed to load data', err);
      } finally {
        setIsLoadingBranches(false);
        setIsLoadingShelves(false);
        setIsLoadingUsers(false);
      }
    };
    void loadData();
  }, [isRoot, selectedTenantId, activeTenant?.id, scanner?.tenantId]);

  const handleSubmitScanner = async (
    values: ScannerFormValues,
  ): Promise<void> => {
    if (!id) return;
    setIsSaving(true);
    setError(null);

    const payload: UpdateScannerPayload = {
      name: values.name,
      deviceId: values.deviceId,
      type: values.type,
      status: values.status,
      branchId: values.branchId,
      shelfId: values.shelfId || undefined,
      assignedUserId: values.assignedUserId || undefined,
      // Include tenantId if root user selected it in form, otherwise backend uses scanner's existing tenantId
      tenantId: isRoot && values.tenantId ? values.tenantId : undefined,
      deviceModelId:
        values.deviceModelId && values.deviceModelId !== '__none__'
          ? values.deviceModelId
          : null,
    };

    try {
      await updateScanner(id, payload);
      await loadScanner();
      toast.success(successUpdate);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : errorUpdate;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!id || !scanner) return;
    setIsDeleting(true);
    try {
      await deleteScanner(id);
      toast.success(successDelete);
      navigate('/scanners');
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : errorDelete;
      setError(errorMessage);
      toast.error(errorMessage);
      setIsDeleting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    void handleSubmit(handleSubmitScanner)();
  };

  if (!id) {
    return (
      <div className="p-6 text-sm text-destructive">
        {errorNotFound}
      </div>
    );
  }

  return (
    <EditPageLayout
      title={editTitle}
      description={scanner ? editDescription : undefined}
      isLoading={isLoading}
      error={error}
      formId="scanner-edit-form"
      onSubmit={handleFormSubmit}
      onCancel={() => navigate(-1)}
      cancelLabel={t('pages.scanners.actions.cancel')}
      onSave={() => void handleSubmit(handleSubmitScanner)()}
      saveLabel={t('pages.scanners.actions.update')}
      savingLabel={t('pages.scanners.actions.updating')}
      isSaving={isSaving}
      onDelete={handleDelete}
      deleteLabel={t('pages.scanners.actions.delete')}
      deletingLabel={t('pages.scanners.actions.deleting')}
      isDeleting={isDeleting}
      deleteDialogTitle={deleteDialogTitle}
      deleteDialogDescription={t('pages.scanners.dialogs.delete.description', {
        name: scanner?.name,
      })}
      entityExists={!!scanner}
    >
      <form
        id="scanner-edit-form"
        className="space-y-6"
        onSubmit={handleSubmit(handleSubmitScanner)}
      >
        <FormSection
          title={t('pages.scanners.form.name')}
          description={t('pages.scanners.form.name')}
          error={
            errors.name
              ? formatErrorMessage(errors.name.message) ??
                (errors.name.message as string)
              : null
          }
        >
          <Input
            id="scanner-name"
            {...register('name')}
            placeholder={t('pages.scanners.form.name')}
            disabled={isSaving}
          />
        </FormSection>
        <FormSection
          title={t('pages.scanners.form.deviceId')}
          description={t('pages.scanners.form.deviceId')}
          error={
            errors.deviceId
              ? formatErrorMessage(errors.deviceId.message) ??
                (errors.deviceId.message as string)
              : null
          }
        >
          <Input
            id="scanner-deviceId"
            {...register('deviceId')}
            placeholder={t('pages.scanners.form.deviceId')}
            disabled={isSaving}
          />
        </FormSection>
        <FormSection
          title={t('pages.scanners.form.type')}
          description={t('pages.scanners.form.type')}
          error={
            errors.type
              ? formatErrorMessage(errors.type.message) ??
                (errors.type.message as string)
              : null
          }
        >
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={isSaving}
              >
                <SelectTrigger id="scanner-type">
                  <SelectValue placeholder={t('pages.scanners.form.type')} />
                </SelectTrigger>
                <SelectContent>
                  {SCANNER_TYPES.map((scannerType) => (
                    <SelectItem
                      key={scannerType.value}
                      value={scannerType.value}
                    >
                      {t(scannerType.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </FormSection>
        <FormSection
          title={t('pages.scanners.form.status')}
          description={t('pages.scanners.form.status')}
        >
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={isSaving}
              >
                <SelectTrigger id="scanner-status">
                  <SelectValue placeholder={t('pages.scanners.form.status')} />
                </SelectTrigger>
                <SelectContent>
                  {SCANNER_STATUSES.map((status) => (
                    <SelectItem
                      key={status.value}
                      value={status.value}
                    >
                      {t(status.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </FormSection>
        <FormSection
          title={t('pages.scanners.form.deviceModel')}
          description={t('pages.scanners.form.selectDeviceModel')}
        >
          <Controller
            name="deviceModelId"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value || '__none__'}
                onValueChange={(value) => field.onChange(value || '__none__')}
                disabled={isSaving}
              >
                <SelectTrigger id="scanner-deviceModel">
                  <SelectValue
                    placeholder={t('pages.scanners.form.selectDeviceModel')}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {deviceModels.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.name} ({model.slug})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </FormSection>
        {isRoot && !activeTenant && (
          <FormSection
            title={t('pages.scanners.form.tenant')}
            description={t('pages.scanners.form.selectTenant')}
            error={
              errors.tenantId
                ? formatErrorMessage(errors.tenantId.message) ??
                  (errors.tenantId.message as string)
                : null
            }
          >
            <Controller
              name="tenantId"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value || undefined}
                  onValueChange={(value) => {
                    field.onChange(value || '');
                    // Reset branch and shelf when tenant changes
                    setValue('branchId', '');
                    setValue('shelfId', '');
                  }}
                  disabled={isSaving || isLoadingTenants}
                >
                  <SelectTrigger id="scanner-tenant">
                    <SelectValue
                      placeholder={t('pages.scanners.form.selectTenant')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {tenants.length === 0 ? (
                      <SelectItem value="none" disabled>
                        {t('pages.scanners.form.selectTenant')}
                      </SelectItem>
                    ) : (
                      tenants.map((tenant) => (
                        <SelectItem key={tenant.id} value={tenant.id}>
                          {tenant.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
            />
          </FormSection>
        )}
        <FormSection
          title={branchLabel}
          description={t('pages.scanners.form.selectBranch')}
          error={
            errors.branchId
              ? formatErrorMessage(errors.branchId.message) ??
                (errors.branchId.message as string)
              : null
          }
        >
          <Controller
            name="branchId"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  // Reset shelf when branch changes
                  setValue('shelfId', '');
                }}
                disabled={isSaving || isLoadingBranches || (isRoot && !activeTenant && !selectedTenantId)}
              >
                <SelectTrigger id="scanner-branch">
                  <SelectValue
                    placeholder={t('pages.scanners.form.selectBranch')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </FormSection>
        <FormSection
          title={shelfLabel}
          description={selectShelfLabel}
        >
          <Controller
            name="shelfId"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value || undefined}
                onValueChange={(value) => field.onChange(value || '')}
                disabled={isSaving || isLoadingShelves || !selectedBranchId}
              >
                <SelectTrigger id="scanner-shelf">
                  <SelectValue
                    placeholder={selectShelfLabel}
                  />
                </SelectTrigger>
                <SelectContent>
                  {availableShelves.length === 0 ? (
                    <SelectItem value="none" disabled>
                      {selectedBranchId
                        ? selectShelfLabel
                        : t('pages.scanners.form.selectBranchFirst')}
                    </SelectItem>
                  ) : (
                    availableShelves.map((shelf) => (
                      <SelectItem key={shelf.id} value={shelf.id}>
                        {shelf.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
          />
        </FormSection>
        <FormSection
          title={t('pages.scanners.form.assignedUser')}
          description={t('pages.scanners.form.selectUser')}
        >
          <Controller
            name="assignedUserId"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value || undefined}
                onValueChange={(value) => field.onChange(value || '')}
                disabled={isSaving || isLoadingUsers}
              >
                <SelectTrigger id="scanner-assignedUser">
                  <SelectValue
                    placeholder={t('pages.scanners.form.selectUser')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {users.length === 0 ? (
                    <SelectItem value="none" disabled>
                      {t('pages.scanners.form.selectUser')}
                    </SelectItem>
                  ) : (
                    users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.firstName} {user.lastName} ({user.email})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
          />
        </FormSection>
      </form>
    </EditPageLayout>
  );
}
